import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { Material } from "./models/Material";
import {
  deleteMaterial,
  downloadMaterial,
  fetchMaterialCategories,
  fetchMaterials,
  materialUploadProgressed,
  updateMaterial,
  uploadMaterial,
} from "./slice-extensions/materials-slice-extension";

interface MaterialsState {
  items: Material[];
  totalCount: number;
  page: number;
  categories: string[];
  status: LoadingStatus;
  uploadStatus: LoadingStatus;
  /** The running upload's progress (0–1), null when none is running. */
  uploadProgress: number | null;
  lastError?: ApiErrorResponse;
}

const initialState: MaterialsState = {
  items: [],
  totalCount: 0,
  page: 1,
  categories: [],
  status: LoadingStatus.Idle,
  uploadStatus: LoadingStatus.Idle,
  uploadProgress: null,
  lastError: undefined,
};

const materialsSlice = createSlice({
  name: "materials",
  initialState,
  reducers: {
    clearMaterialsError: (state) => {
      state.lastError = undefined;
    },
    resetMaterialUpload: (state) => {
      state.uploadStatus = LoadingStatus.Idle;
      state.uploadProgress = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMaterials.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchMaterials.fulfilled, (state, action) => {
        state.status = LoadingStatus.Idle;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
      })
      .addCase(fetchMaterials.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder.addCase(fetchMaterialCategories.fulfilled, (state, action) => {
      state.categories = action.payload;
    });

    builder
      .addCase(uploadMaterial.pending, (state) => {
        state.uploadStatus = LoadingStatus.Loading;
        state.uploadProgress = null;
        state.lastError = undefined;
      })
      .addCase(materialUploadProgressed, (state, action: PayloadAction<number>) => {
        state.uploadProgress = action.payload;
      })
      .addCase(uploadMaterial.fulfilled, (state, action: PayloadAction<Material>) => {
        state.uploadStatus = LoadingStatus.Idle;
        state.uploadProgress = null;
        state.items = [action.payload, ...state.items];
        state.totalCount += 1;
        if (!state.categories.includes(action.payload.category)) {
          state.categories = [...state.categories, action.payload.category].sort();
        }
      })
      .addCase(uploadMaterial.rejected, (state, action) => {
        state.uploadStatus = LoadingStatus.Failed;
        state.uploadProgress = null;
        state.lastError = action.payload;
      });

    builder
      .addCase(updateMaterial.fulfilled, (state, action: PayloadAction<Material>) => {
        state.items = state.items.map((material) =>
          material.id === action.payload.id ? action.payload : material,
        );
        if (!state.categories.includes(action.payload.category)) {
          state.categories = [...state.categories, action.payload.category].sort();
        }
      })
      .addCase(updateMaterial.rejected, (state, action) => {
        state.lastError = action.payload;
      });

    builder
      .addCase(deleteMaterial.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.filter((material) => material.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteMaterial.rejected, (state, action) => {
        state.lastError = action.payload;
      });

    builder
      .addCase(downloadMaterial.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.map((material) =>
          material.id === action.payload
            ? { ...material, downloadCount: material.downloadCount + 1 }
            : material,
        );
      })
      .addCase(downloadMaterial.rejected, (state, action) => {
        state.lastError = action.payload;
      });
  },
});

export const { clearMaterialsError, resetMaterialUpload } = materialsSlice.actions;

export const selectMaterials = (state: RootState) => state.materials.items;
export const selectMaterialsTotalCount = (state: RootState) => state.materials.totalCount;
export const selectMaterialCategories = (state: RootState) => state.materials.categories;
export const selectMaterialsStatus = (state: RootState) => state.materials.status;
export const selectMaterialsUploadStatus = (state: RootState) => state.materials.uploadStatus;
export const selectMaterialsUploadProgress = (state: RootState) => state.materials.uploadProgress;
export const selectMaterialsError = (state: RootState) => state.materials.lastError;

export default materialsSlice.reducer;
